import type { BookingTicket } from '~/types/booking'
import { faDate } from '~/utils/faDate'
import {
  formatAppointmentCodeFa,
  formatRialFa,
  paymentStatusLabel,
  visitTypeLabel,
} from '~/utils/booking/ticketCode'

export const TICKET_SVG_WIDTH = 720

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function truncate(value: string, max = 42): string {
  const text = value.trim()
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}

export function buildTicketRows(ticket: BookingTicket): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [
    { label: 'بیمار', value: ticket.patientName },
    { label: 'خدمت', value: ticket.serviceTitle },
  ]
  if (ticket.doctorName) {
    rows.push({ label: 'پزشک', value: ticket.doctorName })
  }
  rows.push(
    { label: 'نوع نوبت', value: visitTypeLabel(ticket.visitType) },
    { label: 'زمان', value: faDate(ticket.startAt, 'long') },
    { label: 'پرداخت', value: paymentStatusLabel(ticket.paymentStatus, ticket.amountDue) },
  )
  const amount = ticket.paidAmount > 0
    ? ticket.paidAmount
    : ticket.amountDue
  if (amount > 0) {
    rows.push({ label: 'مبلغ', value: formatRialFa(amount) })
  }
  return rows
}

/**
 * Standalone SVG of the appointment ticket (cream paper, RTL).
 * Used for PNG export; text is escaped so names cannot break the markup.
 */
export function buildTicketSvg(ticket: BookingTicket): { svg: string; width: number; height: number } {
  const width = TICKET_SVG_WIDTH
  const padX = 56
  const rows = buildTicketRows(ticket)
  const headerH = 210
  const rowH = 38
  const footerH = 90
  const height = headerH + rows.length * rowH + footerH
  const right = width - padX
  const paper = '#faf8f2'
  const ink = '#2f3140'
  const muted = '#6b6e7c'
  const line = '#d9d4c8'
  const accent = '#2563eb'
  const font = "IranYekan, Tahoma, 'Noto Naskh Arabic', 'Segoe UI', sans-serif"

  const rowMarkup = rows.map((row, index) => {
    const y = headerH + index * rowH
    return `
      <text x="${right}" y="${y}" text-anchor="end" fill="${muted}" font-size="16">${escapeXml(row.label)}</text>
      <text x="${padX}" y="${y}" text-anchor="start" fill="${ink}" font-size="16" font-weight="600">${escapeXml(truncate(row.value))}</text>
    `
  }).join('')

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="${width}" height="${height}" fill="${paper}"/>
  <text x="${width / 2}" y="56" text-anchor="middle" fill="${muted}" font-family="${font}" font-size="14" font-weight="700" letter-spacing="3">${escapeXml((ticket.siteTitle || 'نوبت').trim() || 'نوبت')}</text>
  <text x="${width / 2}" y="92" text-anchor="middle" fill="${ink}" font-family="${font}" font-size="22" font-weight="700">تیکت نوبت</text>
  <text x="${width / 2}" y="148" text-anchor="middle" fill="${accent}" font-family="${font}" font-size="28" font-weight="700">${escapeXml(formatAppointmentCodeFa(ticket.code))}</text>
  <text x="${width / 2}" y="174" text-anchor="middle" fill="${muted}" font-family="${font}" font-size="13">شماره پیگیری</text>
  <line x1="${padX}" y1="196" x2="${right}" y2="196" stroke="${line}" stroke-width="1" stroke-dasharray="6 6"/>
  <g font-family="${font}">${rowMarkup}</g>
  <line x1="${padX}" y1="${headerH + rows.length * rowH - 10}" x2="${right}" y2="${headerH + rows.length * rowH - 10}" stroke="${line}" stroke-width="1" stroke-dasharray="6 6"/>
  <text x="${width / 2}" y="${height - 36}" text-anchor="middle" fill="${muted}" font-family="${font}" font-size="14">این تیکت را هنگام مراجعه همراه داشته باشید</text>
</svg>`

  return { svg, width, height }
}
